import { Directive, computed, input } from '@angular/core';
import { cva, type VariantProps } from 'class-variance-authority';

export const navtabVariants = cva(
  'relative items-center whitespace-nowrap rounded-[var(--r-s)] font-semibold text-c3 ' +
    'transition-colors duration-[var(--t-fast)] ease-[var(--ease-smooth)] ' +
    // The button's focus ring, keyboard only: without it the browser paints its own outline.
    'outline-none focus-visible:ring-1 focus-visible:ring-ring ' +
    // hover recolour must not beat the active state on equal specificity — scope it to non-active
    '[&:not(.tab-on)]:hover:text-ink ' +
    "after:absolute after:bottom-0 after:h-0.5 after:content-[''] " +
    'after:rounded-t-xs after:bg-acc after:opacity-0 [&.tab-on]:after:opacity-100',
  {
    variants: {
      display: {
        always: 'flex',
        lgOnly: 'hidden lg:flex',
      },
      // Height, type scale, insets and the active tone travel together: a size that mixed its own
      // scale with the other one's would emit two competing `text-*`/`h-*` utilities on one element.
      size: {
        /** The top bar: the tab fills the bar's height and lights up in the accent. */
        default: 'h-full px-3.5 text-navtab after:left-3.25 after:right-3.25 [&.tab-on]:text-acc2',
        /** In-page sub-tabs: the same tab, one step down, active in the reading tone. */
        sm: 'h-7 px-2.5 text-sm after:left-2.5 after:right-2.5 [&.tab-on]:text-ink',
      },
    },
    defaultVariants: { display: 'always', size: 'default' },
  },
);
export type NavtabVariants = VariantProps<typeof navtabVariants>;

/**
 * The one tab look for every top-bar nav (photographer shell, album editor, student nav).
 * Active state = the `tab-on` class (toggled by routerLinkActive or set statically).
 */
@Directive({
  selector: '[hlmNavtab]',
  host: { '[class]': 'cls()' },
})
export class HlmNavtab {
  readonly hlmNavtab = input<'' | NonNullable<NavtabVariants['display']>>('');
  readonly navtabSize = input<NonNullable<NavtabVariants['size']>>('default');

  protected readonly cls = computed(() => navtabVariants({ display: this.hlmNavtab() || 'always', size: this.navtabSize() }));
}
